/** /pre-trip-items/:id/:tab? — pre-trip item editor shell. Tabs: General +
 *  Used by. */
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Tab, Tabs } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as itemsApi from '@/api/preTripItems';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { PageHeader } from '@/components/PageHeader';
import { IdentitySubtitle } from '@/components/ValueActions/identity';
import { preTripItemsHref } from '@/utils/gridLinks';
import { PermissionGate } from '@/components/PermissionGate';
import { useSnackbar } from '@/components/Snackbar';
import { UsedByPanel } from '@/components/UsedByPanel/UsedByPanel';
import { usePermissions } from '@/hooks/usePermissions';

import { PreTripItemGeneralTab } from './tabs/PreTripItemGeneralTab';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'used-by', label: 'Used by' },
] as const;

const MODE_LABELS: Record<string, string> = { link: 'Link', file: 'File', text: 'Text' };

export function PreTripItemDetailPage() {
  const { id: idStr, tab } = useParams<{ id: string; tab?: string }>();
  const id = Number(idStr);
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const {
    data: item,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['pre_trip_items', 'detail', id],
    queryFn: () => itemsApi.retrieve(id),
    enabled: Number.isFinite(id),
  });
  const onSaved = () => void refetch();

  const deleteMutation = useMutation({
    mutationFn: () => itemsApi.remove(id),
    onSuccess: () => {
      snackbar.show('Item deleted.', 'success');
      qc.invalidateQueries({ queryKey: ['pre_trip_items'] });
      navigate('/pre-trip-items');
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error'),
  });

  const canViewHistory = hasPerm('console_audit_view');
  const tabs = useMemo(
    () => (canViewHistory ? [...TABS, { id: 'history', label: 'History' } as const] : [...TABS]),
    [canViewHistory],
  );
  const activeTab = useMemo(() => tabs.find((t) => t.id === tab)?.id ?? 'general', [tabs, tab]);

  if (!Number.isFinite(id)) return <Alert severity="warning">Invalid item id.</Alert>;
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !item) return <Alert severity="error">Couldn’t load item.</Alert>;

  return (
    <Box>
      <PageHeader
        helpStory="pre-trip-items"
        section={tabs.find((t) => t.id === activeTab)?.label}
        title={item.name}
        subtitle={
          <IdentitySubtitle
            tenant={item.tenant}
            grid={item.tenant ? { href: preTripItemsHref({ tenant: item.tenant.id }), label: 'Trip items of this operator' } : null}
            id={item.id}
          />
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              variant="outlined"
              label={MODE_LABELS[item.display_mode] ?? item.display_mode}
            />
            <PermissionGate perm="console_pretrip_manage">
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </Button>
            </PermissionGate>
          </Stack>
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => navigate(`/pre-trip-items/${id}/${v}`)}>
          {tabs.map((t) => (
            <Tab key={t.id} value={t.id} label={t.label} />
          ))}
        </Tabs>
      </Box>

      <TabPane show={activeTab === 'general'}>
        <PreTripItemGeneralTab item={item} onSaved={onSaved} />
      </TabPane>
      <TabPane show={activeTab === 'used-by'}>
        <UsedByPanel resource="pre_trip_items" id={item.id} />
      </TabPane>
      <TabPane show={activeTab === 'history'}>
        <HistoryPanel contentType="demo.pre_trip_item" objectPk={String(item.id)} onRestored={onSaved} />
      </TabPane>

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        <RouterLink to={preTripItemsHref({ tenant: item.tenant?.id })} style={{ color: '#0a1a3f' }}>
          ← Back to trip items
        </RouterLink>
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this item?"
        message={`"${item.name}" will be deleted. This cannot be undone.`}
        confirmLabel="Delete item"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}

function TabPane({ show, children }: { show: boolean; children: ReactNode }) {
  if (!show) return null;
  return <Box>{children}</Box>;
}
